import axios, { CancelTokenSource, InternalAxiosRequestConfig } from 'axios';
import { TokenService } from '../services/secureTokenService';
import { authReadyGate } from '../services/authReadyGate';
import { trackException } from '../utils/monitoring';
import { PersonalUser } from '../interfaces/account';
import { TeamUser } from '../interfaces/team';
import { isNetworkError } from '../utils/errorHandling';
import { getStoredReferrerData } from '../utils/utmTracking';

interface CreateAxiosClientParams {
  baseURL?: string;
  prefix?: string;
  asUser?: TeamUser | PersonalUser | null;
  /**
   * If true, skip waiting for auth gate (used for unauthenticated endpoints)
   */
  skipAuthGate?: boolean;
}

// Extend axios config to include our custom properties
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _skipAuthGate?: boolean;
}

// Global flag to prevent multiple simultaneous reloads when multiple 401s occur
let isHandlingAuthFailure = false;

// Track all pending requests so we can cancel them on auth failure
const pendingRequests = new Set<CancelTokenSource>();

/**
 * Reset auth failure flag - called when starting fresh authentication
 * This prevents stale flag from blocking legitimate auth flows
 */
const resetAuthFailureState = (): void => {
  isHandlingAuthFailure = false;
  pendingRequests.clear();
};

// Function to create an Axios instance with a configurable baseURL
const createAxiosClient = ({
  baseURL,
  prefix = '/v1',
  asUser,
  skipAuthGate = false,
}: CreateAxiosClientParams) => {
  const client = axios.create({
    // Always use the full URL by default
    baseURL: baseURL ? baseURL : process.env.REACT_APP_API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor - ASYNC to support auth gate waiting
  client.interceptors.request.use(
    async (config: ExtendedAxiosRequestConfig) => {
      // Create cancel token for this request
      const cancelTokenSource = axios.CancelToken.source();
      config.cancelToken = cancelTokenSource.token;
      pendingRequests.add(cancelTokenSource);

      // Add prefix to URL if it doesn't already have it
      if (config.url && !config.url.startsWith(prefix)) {
        config.url = `${prefix}${config.url}`;
      }

      // CRITICAL: Wait for auth to be ready before proceeding
      // This prevents API calls from going out before tokens are available
      // Skip if explicitly configured (for unauthenticated endpoints)
      const shouldSkipGate = skipAuthGate || config._skipAuthGate === true;

      if (!shouldSkipGate) {
        try {
          // Wait up to 30 seconds for auth to be ready
          // This is generous but prevents infinite hangs
          await authReadyGate.waitUntilReady(30000);
        } catch (error) {
          // Auth failed or timed out - cancel this request
          console.error('[Axios] Auth gate failed:', error);
          pendingRequests.delete(cancelTokenSource);
          throw new axios.Cancel('Authentication not ready');
        }
      }

      // Add authorization token (will be available now that auth gate passed)
      const accessToken = TokenService.getAccessToken();
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }

      // Add team context headers
      try {
        // Import store lazily to avoid circular dependencies
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const { default: store } = require('../app/store');
        const currentTeam = store.getState().team.currentTeam.record;
        if (!('cirra_organization_id' in config.headers)) {
          if (asUser) {
            config.headers['cirra_organization_id'] =
              'cirra_organization_id' in asUser
                ? asUser.cirra_organization_id || null
                : null;
          } else if (currentTeam) {
            config.headers['cirra_organization_id'] =
              currentTeam.cirra_organization_id;
          }
        }

        if (!asUser) {
          // Add Salesforce organization context headers
          const orgInfo = store.getState().organization.org;
          if (orgInfo) {
            const isPersonalOrg =
              !orgInfo.cirra_organization_id &&
              !currentTeam?.cirra_organization_id;
            const isMatchingOrg =
              orgInfo.cirra_organization_id ===
              currentTeam?.cirra_organization_id;

            if (isPersonalOrg || isMatchingOrg) {
              config.headers['organization_id'] =
                config.headers['organization_id'] ?? orgInfo.organization_id;
              config.headers['user_id'] =
                config.headers['user_id'] ?? orgInfo.user_id;
            }
          }
        }
      } catch (error) {
        // Store might not be available during app initialization
        console.warn('Could not access store for headers:', error);
      }

      // Add UTM attribution headers for new user signup tracking.
      // The backend uses these to update the HubSpot contact with UTM data.
      const signupData = getStoredReferrerData();
      if (signupData) {
        if (signupData.utmSource)
          config.headers['x-utm-source'] = signupData.utmSource;
        if (signupData.utmMedium)
          config.headers['x-utm-medium'] = signupData.utmMedium;
        if (signupData.utmCampaign)
          config.headers['x-utm-campaign'] = signupData.utmCampaign;
        if (signupData.utmTerm)
          config.headers['x-utm-term'] = signupData.utmTerm;
        if (signupData.utmContent)
          config.headers['x-utm-content'] = signupData.utmContent;
        if (signupData.referrer)
          config.headers['x-referrer'] = signupData.referrer;
        if (signupData.gclid) config.headers['x-gclid'] = signupData.gclid;
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  client.interceptors.response.use(
    (response) => {
      // Remove this request from pending set on success
      if (response.config.cancelToken) {
        const source = Array.from(pendingRequests).find(
          (s) => s.token === response.config.cancelToken
        );
        if (source) {
          pendingRequests.delete(source);
        }
      }
      return response;
    },
    async (error) => {
      // Remove this request from pending set on error
      if (error.config?.cancelToken) {
        const source = Array.from(pendingRequests).find(
          (s) => s.token === error.config.cancelToken
        );
        if (source) {
          pendingRequests.delete(source);
        }
      }

      // Handle 401 unauthorized
      if (error.response && error.response.status === 401) {
        // CRITICAL: During initialization, let AuthenticationGuard handle 401s
        // The gate being in initializing state means we're still setting up auth
        // and the health check should handle failures, not trigger a reload loop
        if (authReadyGate.isInitializing()) {
          console.debug(
            '[Axios] 401 during initialization - letting AuthenticationGuard handle it'
          );
          return Promise.reject(error);
        }

        // Only handle auth failure once, even if multiple 401s occur simultaneously
        if (!isHandlingAuthFailure) {
          isHandlingAuthFailure = true;

          console.debug(
            '[Axios] 401 Unauthorized during active session - token expired, redirecting to login'
          );

          // Cancel all pending requests to prevent more 401s
          pendingRequests.forEach((source) => {
            source.cancel('Authentication failed - request cancelled');
          });
          pendingRequests.clear();

          // Mark auth gate as failed so new requests don't try to proceed
          authReadyGate.markFailed('401_unauthorized');

          // Clear tokens
          TokenService.clearTokens('unauthorized');

          // Hard redirect to root - Auth0 will handle login
          // Use window.location.href (not reload) to avoid potential loops
          window.location.href = window.location.origin;
        }

        // Reject with cancelled error to prevent component error handlers from showing errors
        return Promise.reject(new axios.Cancel('Auth failed'));
      }

      // Log non-network errors for debugging
      if (!isNetworkError(error) && !axios.isCancel(error)) {
        // Track non-network errors for monitoring (but not cancelled requests)
        trackException(new Error('Axios API call error: ' + error));
      }

      return Promise.reject(error);
    }
  );

  return client;
};

/**
 * Create an axios instance that skips the auth gate
 * Use for requests that should proceed even during initialization
 * (e.g., the health check in AuthenticationGuard)
 */
const createUnauthenticatedAxiosClient = (
  params: Omit<CreateAxiosClientParams, 'skipAuthGate'> = {}
) => {
  return createAxiosClient({ ...params, skipAuthGate: true });
};

// Default axios client using the default base URL
const axiosClient = createAxiosClient({});

// Export the function to create a custom axios client
export {
  axiosClient,
  createAxiosClient,
  createUnauthenticatedAxiosClient,
  resetAuthFailureState,
};
